import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Globe,
  Server,
  XCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import type {
  NetworkStatusResponse,
  StatusComponent,
  StatusCountry,
  StatusNetwork,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { StatTile } from '@/components/ui/stat-tile';
import {
  Skeleton,
  SkeletonCard,
  SkeletonGrid,
  SkeletonLine,
  SkeletonPage,
  SkeletonStats,
} from '@/components/ui/skeleton';
import { getWebApp } from '@/lib/telegram';
import { NetworkPulse } from '@/components/status/NetworkPulse';
import { Flag } from '@/components/ui/flag';

export const Route = createFileRoute('/status')({ component: StatusPage });

/** Сколько дней истории рисуем. Бэк отдаёт 90, но на телефоне 90 полосок — это
 *  по 2px каждая, нечитаемая каша. Процент аптайма при этом остаётся за 90 дней. */
const VISIBLE_DAYS = 30;

type StatusKey = 'operational' | 'degraded' | 'down' | 'no_data';

const STATUS_META: Record<StatusKey, { dot: string; text: string; bar: string; label: string }> = {
  operational: { dot: 'bg-money', text: 'text-money', bar: 'bg-money', label: 'Работает' },
  degraded: { dot: 'bg-amber', text: 'text-amber', bar: 'bg-amber', label: 'Сбои' },
  down: { dot: 'bg-danger', text: 'text-danger', bar: 'bg-danger', label: 'Недоступно' },
  no_data: { dot: 'bg-white/25', text: 'text-dim', bar: 'bg-white/[0.08]', label: 'Нет данных' },
};

const OVERALL_META: Record<
  StatusKey,
  { title: string; Icon: typeof CheckCircle2; glow: string; text: string }
> = {
  operational: { title: 'Все системы работают', Icon: CheckCircle2, glow: 'bg-money', text: 'text-money' },
  degraded: { title: 'Частичные сбои', Icon: AlertTriangle, glow: 'bg-amber', text: 'text-amber' },
  down: { title: 'Серьёзный сбой', Icon: XCircle, glow: 'bg-danger', text: 'text-danger' },
  no_data: { title: 'Собираем данные', Icon: Activity, glow: 'bg-white/40', text: 'text-dim' },
};

function asKey(s: string | undefined): StatusKey {
  return s === 'operational' || s === 'degraded' || s === 'down' ? s : 'no_data';
}

function relTime(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff) || diff < 0) return 'только что';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  return `${Math.floor(h / 24)} дн назад`;
}

function StatusPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    tg.MainButton?.hide();
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const { data, isPending } = useQuery({
    queryKey: ['network-status'],
    queryFn: () => api.networkStatus(),
    refetchInterval: 60_000,
    // Экран свёрнут или бот в фоне — не дёргаем сеть впустую.
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });

  if (isPending) return <StatusSkeleton />;
  if (!data) {
    return (
      <div className="p-4 pb-24">
        <Card className="p-8 text-center text-sm text-dim">
          Статус временно недоступен. Потяните экран, чтобы обновить.
        </Card>
      </div>
    );
  }
  return <StatusView data={data} />;
}

function StatusView({ data }: { data: NetworkStatusResponse }) {
  const overall = asKey(data.overall);
  const meta = OVERALL_META[overall];
  const net = data.network;
  // Сеть «Навсегда» приходит, только пока раздел работает. Плитки наверху остаются
  // про «На срок», и рядом со второй сетью это подписано: иначе их читают как общие.
  const lifetime = data.lifetime_network ?? null;
  const termHint = lifetime ? <div className="mt-0.5 text-[11px] text-dim">сеть «На срок»</div> : null;

  const avgUptime = (() => {
    const vals = data.components.map((c) => c.uptime_pct_90d).filter((v): v is number => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  })();

  return (
    <div className="p-4 pb-24 space-y-5">
      {/* Hero: карта-пульс + статус и ключевые цифры поверх */}
      <div className="relative overflow-hidden rounded-[24px] border border-border">
        <NetworkPulse className="absolute inset-0 h-full w-full opacity-70" />
        <div className="relative z-10 p-5">
          <div className="flex items-center gap-2.5">
            <span className="relative flex size-2.5">
              <span className={cn('absolute inline-flex size-full animate-ping rounded-full opacity-60', meta.glow)} />
              <span className={cn('relative inline-flex size-2.5 rounded-full', meta.glow)} />
            </span>
            <meta.Icon className={cn('size-4', meta.text)} />
            <h1 className="font-display text-lg font-bold">{meta.title}</h1>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <StatTile overline="Локаций" value={net.countries} tone="primary">
              {termHint}
            </StatTile>
            <StatTile overline="Серверов" value={net.servers} tone="neutral">
              {termHint}
            </StatTile>
            <StatTile
              overline="Узлов онлайн"
              value={net.checked_at && net.nodes_total > 0 ? `${net.nodes_up}/${net.nodes_total}` : '—'}
              tone="money"
            >
              {termHint}
            </StatTile>
            <StatTile
              overline="Аптайм 90 дней"
              value={avgUptime != null ? `${avgUptime.toFixed(2)}%` : '—'}
              tone="violet"
            />
          </div>
        </div>
      </div>

      {/* Состояние сервисов */}
      <section className="space-y-2.5">
        <h2 className="eyebrow px-1">Состояние сервисов</h2>
        <Card className="divide-y divide-border p-0 overflow-hidden">
          {data.components.map((c) => (
            <ComponentRow key={c.id} component={c} />
          ))}
        </Card>
      </section>

      {/* VPN-серверы: одна сеть или две, «На срок» и «Навсегда» */}
      <NetworkSection
        title={lifetime ? 'Сеть «На срок»' : 'VPN-серверы'}
        net={net}
        summary={lifetime ? networkSummary(net) : undefined}
        empty={Object.keys(net.regions).length === 0}
        emptyText="Список серверов появится после ближайшей синхронизации."
      />
      {lifetime && (
        <NetworkSection
          title="Сеть «Навсегда»"
          net={lifetime}
          summary={networkSummary(lifetime)}
          empty={lifetime.servers === 0 || Object.keys(lifetime.regions).length === 0}
          emptyText="Список серверов «Навсегда» ещё не собран."
        />
      )}

      <p className="text-center text-[11px] text-dim">
        {data.updated_at
          ? `Проверено ${relTime(data.updated_at)} · обновляется автоматически`
          : 'Данные собираются'}
      </p>
    </div>
  );
}

/**
 * Блок одной сети: регионы, страны, серверы с пингом. Рисуется на каждую сеть:
 * «На срок» всегда, «Навсегда» пока её раздел работает. `summary` появляется,
 * только когда сетей две: с одной её цифры и так стоят в плитках наверху.
 */
function NetworkSection({
  title,
  net,
  summary,
  empty,
  emptyText,
}: {
  title: string;
  net: StatusNetwork;
  summary?: string;
  empty: boolean;
  emptyText: string;
}) {
  return (
    <section className="space-y-2.5">
      <div className="px-1">
        <div className="flex items-center justify-between">
          <h2 className="eyebrow">{title}</h2>
          {net.checked_at && <span className="text-[11px] text-dim">пинг {relTime(net.checked_at)}</span>}
        </div>
        {summary ? <p className="mt-0.5 text-[11px] text-dim">{summary}</p> : null}
      </div>
      {empty ? (
        <Card className="p-6 text-center text-sm text-dim">{emptyText}</Card>
      ) : (
        <div className="space-y-3">
          {Object.entries(net.regions).map(([region, countries]) => (
            <Card key={region} className="p-3">
              <div className="mb-2 flex items-center gap-2 px-1">
                <Globe className="size-3.5 text-primary-light" />
                <h3 className="font-display text-[13px] font-semibold">{region}</h3>
                <span className="ml-auto text-[11px] text-dim">{countries.length}</span>
              </div>
              <div className="space-y-1">
                {countries.map((c) => (
                  <CountryRow key={c.flag + c.country_ru} country={c} />
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

/** Цифры сети для шапки её блока: «12 стран · 30 из 32 серверов в сети». */
function networkSummary(n: StatusNetwork): string {
  const parts: string[] = [];
  if (n.countries > 0) {
    parts.push(`${n.countries} ${plural(n.countries, 'страна', 'страны', 'стран')}`);
  }
  if (n.checked_at && n.nodes_total > 0) {
    // «из 21 сервера», «из 32 серверов»: после «из» родительный падеж.
    const of = plural(n.nodes_total, 'сервера', 'серверов', 'серверов');
    parts.push(`${n.nodes_up} из ${n.nodes_total} ${of} в сети`);
  } else if (n.servers > 0) {
    parts.push(`${n.servers} ${plural(n.servers, 'сервер', 'сервера', 'серверов')}`);
  }
  return parts.join(' · ');
}

/** Русское склонение: 1 страна, 2 страны, 5 стран, 21 страна. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function ComponentRow({ component }: { component: StatusComponent }) {
  const key = asKey(component.status);
  const meta = STATUS_META[key];
  return (
    <div className="p-3.5">
      <div className="mb-2 flex items-center gap-2">
        <span className={cn('size-2 shrink-0 rounded-full', meta.dot)} />
        <span className="text-[13px] font-medium">{component.label}</span>
        <span className="ml-auto flex items-center gap-2 text-[11px]">
          {component.latency_ms != null && <span className="nums text-dim">{component.latency_ms} мс</span>}
          <span className={cn('font-semibold', meta.text)}>
            {component.uptime_pct_90d != null ? `${component.uptime_pct_90d.toFixed(2)}%` : meta.label}
          </span>
        </span>
      </div>
      <UptimeBar days={component.days} />
      <div className="mt-1 flex justify-between text-[10px] text-dim/70">
        <span>{VISIBLE_DAYS} дней назад</span>
        <span>сегодня</span>
      </div>
    </div>
  );
}

function UptimeBar({ days }: { days: StatusComponent['days'] }) {
  return (
    <div className="flex h-6 items-stretch gap-[2px]">
      {days.slice(-VISIBLE_DAYS).map((d) => (
        <div
          key={d.day}
          title={d.status === 'no_data' ? `${d.day}: нет данных` : `${d.day}: ${d.pct.toFixed(2)}%`}
          className={cn('h-full min-w-[2px] flex-1 rounded-[2px]', STATUS_META[asKey(d.status)].bar)}
        />
      ))}
    </div>
  );
}

function CountryRow({ country }: { country: StatusCountry }) {
  const hasServers = country.servers.length > 0;
  // «Измерено» = сервер с реальным up (true/false); up===null — ещё не пинговали.
  // Пока пинга не было — серый, а не красный (иначе выглядит как тотальный отказ).
  const measured = country.servers.filter((s) => s.up !== null).length;
  const down = country.servers.filter((s) => s.up === false).length;
  const dot =
    measured === 0 ? 'bg-white/25' : down === 0 ? 'bg-money' : down < measured ? 'bg-amber' : 'bg-danger';

  const upLatencies = country.servers
    .filter((s) => s.up && s.latency_ms != null)
    .map((s) => s.latency_ms as number);
  const avgLat = upLatencies.length
    ? Math.round(upLatencies.reduce((a, b) => a + b, 0) / upLatencies.length)
    : null;

  const summary = (
    <div className="flex w-full items-center gap-2.5">
      <span className={cn('size-2 shrink-0 rounded-full', dot)} />
      <span className="text-lg leading-none">
        <Flag flag={country.flag} code={country.country_code} />
      </span>
      <span className="text-[13px] font-medium">{country.country_ru}</span>
      <span className="ml-auto flex items-center gap-2.5 text-[11px]">
        {avgLat != null && <span className="nums text-dim">{avgLat} мс</span>}
        {measured > 0 && (
          <span className="nums text-dim">
            {country.up}/{country.total}
          </span>
        )}
        {hasServers && <ChevronDown className="size-3.5 text-dim transition-transform group-open:rotate-180" />}
      </span>
    </div>
  );

  // min-h-11 = 44px — минимальный тач-таргет, строка кликабельна целиком.
  if (!hasServers) {
    return <div className="flex min-h-11 items-center rounded-[16px] px-2">{summary}</div>;
  }

  return (
    <details className="group rounded-[16px] transition-colors open:bg-white/[0.03]">
      <summary className="flex min-h-11 cursor-pointer list-none items-center px-2">{summary}</summary>
      <div className="grid grid-cols-1 gap-1 px-2 pb-2 pt-1">
        {country.servers.map((s, i) => {
          const sdot = s.up === true ? 'bg-money' : s.up === false ? 'bg-danger' : 'bg-white/25';
          return (
            <div
              key={i}
              className="flex items-center gap-2 rounded-[12px] bg-white/[0.04] px-2.5 py-2"
            >
              <span className={cn('size-1.5 shrink-0 rounded-full', sdot)} />
              <span className="truncate text-[11px] text-muted-foreground">{s.remark ?? 'сервер'}</span>
              <span className="nums ml-auto shrink-0 text-[11px] text-dim">
                {s.up === true && s.latency_ms != null ? `${s.latency_ms} мс` : s.up === false ? 'офлайн' : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </details>
  );
}

/** Hero со статусом и четырьмя плитками, затем список сервисов и серверов. */
function StatusSkeleton() {
  return (
    <SkeletonPage className="space-y-5">
      <SkeletonCard className="rounded-[24px] min-h-0 p-5 space-y-4">
        <div className="flex items-center gap-2.5">
          <Skeleton className="size-2.5 rounded-full" />
          <Skeleton className="size-4 rounded" />
          <SkeletonLine size="title" className="w-40" />
        </div>
        <SkeletonStats count={4} cols={2} className="gap-2.5" />
      </SkeletonCard>

      <section className="space-y-2.5">
        <h2 className="eyebrow px-1">Состояние сервисов</h2>
        <SkeletonCard className="rounded-[24px] min-h-0 divide-y divide-border">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-4">
              <SkeletonLine className="w-1/3" />
              <Skeleton className="h-[22px] w-20 rounded-full" />
            </div>
          ))}
        </SkeletonCard>
      </section>

      <section className="space-y-2.5">
        <h2 className="eyebrow px-1">VPN-серверы</h2>
        <SkeletonCard className="rounded-[24px] min-h-0 p-3 space-y-2">
          <SkeletonLine size="xs" className="w-24 mx-1" />
          <SkeletonGrid count={4} cols={2} variant="flat" className="gap-2" itemClassName="h-11 rounded-xl" />
        </SkeletonCard>
      </section>
    </SkeletonPage>
  );
}
